import { forwardRef } from "react";
import type { InputHTMLAttributes } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, className = "", id, ...rest }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-stone-700 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-stone-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`
              block w-full rounded-lg border text-sm transition-colors
              placeholder:text-stone-400
              ${leftIcon ? "pl-10" : "pl-3"}
              pr-3 py-2.5
              ${
                error
                  ? "border-red-400 bg-red-50 focus:ring-red-400"
                  : "border-stone-300 bg-white focus:ring-forest-500"
              }
              focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent
              disabled:bg-stone-50 disabled:text-stone-400 disabled:cursor-not-allowed
              ${className}
            `}
            {...rest}
          />
        </div>
        {hint && !error && <p className="mt-1.5 text-xs text-stone-400">{hint}</p>}
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
      </div>
    );
  }
);

Input.displayName = "Input";
